/// Product main ///
(function() {
'use strict';

// Helper: Check if element is inside a featured-product section
function isInsideFeaturedProduct(element) {
  return element && element.closest('[data-fp-section]') !== null;
}

// Helper: Query elements excluding those inside featured-product sections
function queryProductElements(selector, scope) {
  var container = scope || document;
  var elements = container.querySelectorAll(selector);
  return Array.from(elements).filter(function(el) {
    return !isInsideFeaturedProduct(el);
  });
}

// Helper: Query single element excluding featured-product sections
function queryProductElement(selector, scope) {
  var container = scope || document;
  var elements = container.querySelectorAll(selector);
  for (var i = 0; i < elements.length; i++) {
    if (!isInsideFeaturedProduct(elements[i])) {
      return elements[i];
    }
  }
  return null;
}

let productVariantsCache = null;
let selectedOptions = {};
let selectedOptionsInitialized = false;
let currentVariant = null;

// Quantity limit state
let cartItemsCache = null; // cached cart items from /cart.js

let galleryElement = null;
let galleryItems = [];
let navDotsContainer = null;
let prevArrow = null;
let nextArrow = null;
let currentIndex = 0;

let productHeightsInitialized = false;
let singleImageAnimated = false;
let galleryMode = 'arrows'; // 'arrows' or 'vertical-scroll'

// Mobile gallery touch handlers (stored for cleanup)
let mobileTouchStartHandler = null;
let mobileTouchMoveHandler = null;
let mobileTouchEndHandler = null;
let mobileGalleryInitialized = false;

// Arrow/dot handlers (stored for cleanup)
let prevArrowClickHandler = null;
let nextArrowClickHandler = null;
let galleryClickHandler = null;
let galleryMouseMoveHandler = null;

// Infinite carousel for mobile
let infiniteCarouselEnabled = false;
let realImageCount = 0; // Original image count (without clones)
let cloneFirst = null;
let cloneLast = null;

// Helper: get the distance between slide starts (slide width + gap)
function getSlideStep() {
  if (!galleryElement) return 0;
  var w = galleryElement.offsetWidth;
  var gap = parseFloat(getComputedStyle(galleryElement).columnGap) || 0;
  return w + gap;
}

// Helper: get the media element (img/video) inside a gallery-slide
function getSlideMedia(slide) {
  if (!slide) return null;
  return slide.querySelector('.product__item-img') || slide.querySelector('.product__item-video') || slide.querySelector('video');
}

// Mobile dynamic height helpers
function isMobileViewport() {
  return window.innerWidth < 750;
}

// Safe center: center content when it fits, align to top when it overflows
function applySafeCenter() {
  if (isMobileViewport()) return;

  var productInfoMain = queryProductElement('.product-info__main');
  var productInfoPc = queryProductElement('.product-info__pc');

  // Check .product-info__main
  if (productInfoMain) {
    var contentHeight = productInfoMain.scrollHeight;
    var containerHeight = productInfoMain.clientHeight;

    if (contentHeight > containerHeight + 5) {
      // Content overflows - align to top
      productInfoMain.style.justifyContent = 'flex-start';
    } else {
      // Content fits - center it
      productInfoMain.style.justifyContent = 'center';
    }
  }

  // Check .product-info__pc (for three-columns layout)
  if (productInfoPc && getComputedStyle(productInfoPc).display !== 'none') {
    var pcContentHeight = productInfoPc.scrollHeight;
    var pcContainerHeight = productInfoPc.clientHeight;

    if (pcContentHeight > pcContainerHeight + 5) {
      productInfoPc.style.justifyContent = 'flex-start';
    } else {
      productInfoPc.style.justifyContent = 'center';
    }
  }
}

function getImageHeight(index) {
  if (!galleryItems || !galleryItems[index]) return 0;
  var slide = galleryItems[index];
  var h = slide.offsetHeight || 0;
  if (h > 50) return h;
  // Fallback: use gallery height to prevent collapse
  if (galleryElement && galleryElement.offsetHeight > 50) {
    return galleryElement.offsetHeight;
  }
  return 0;
}

function setGalleryHeight(height, animate) {
  if (!galleryElement || !isMobileViewport() || height <= 0) return;

  // First time: instant switch from aspect-ratio to real height (no visible transition)
  if (!galleryElement.style.height) {
    galleryElement.style.transition = 'none';
    galleryElement.style.height = height + 'px';
    return;
  }

  if (animate) {
    galleryElement.style.transition = 'height 0.3s ease';
  } else {
    galleryElement.style.transition = 'none';
  }
  galleryElement.style.height = height + 'px';
}

function updateMobileGalleryHeight() {
  if (!isMobileViewport()) {
    if (galleryElement) {
      galleryElement.style.height = '';
      galleryElement.style.transition = '';
    }
    return;
  }
  setGalleryHeight(getImageHeight(currentIndex), true);
}

/// Helpers ///
function getProductVariants() {
  if (Array.isArray(productVariantsCache) && productVariantsCache.length) {
    return productVariantsCache;
  }
  if (Array.isArray(window.productVariants)) {
    productVariantsCache = window.productVariants;
  } else {
    productVariantsCache = [];
  }
  return productVariantsCache;
}

function getCurrencySymbol() {
  return window.currencySymbol || '';
}

function getProductStrings() {
  const fallback = {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    confirm: 'Confirm'
  };
  if (!window.productStrings) {
    return fallback;
  }
  return {
    addToCart: window.productStrings.addToCart || fallback.addToCart,
    soldOut: window.productStrings.soldOut || fallback.soldOut,
    confirm: window.productStrings.confirm || fallback.confirm
  };
}

/// Product gallery ///
function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
}

function updateDots() {
  if (!navDotsContainer) return;
  const dots = navDotsContainer.querySelectorAll('.nav-dot');
  const activeIndex = infiniteCarouselEnabled ? getRealIndex() : currentIndex;
  dots.forEach(function (dot, index) {
    dot.classList.toggle('active', index === activeIndex);
  });
}

function updateCursor(targetElement, event) {
  // Apply cursor classes to gallery element (the whole clickable area)
  const cursorTarget = galleryElement || targetElement;
  if (!cursorTarget) return;

  if (galleryItems.length > 1 && event) {
    const rect = cursorTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    if (x < rect.width / 2) {
      cursorTarget.classList.add('cursor-left');
      cursorTarget.classList.remove('cursor-right');
    } else {
      cursorTarget.classList.add('cursor-right');
      cursorTarget.classList.remove('cursor-left');
    }
  } else {
    cursorTarget.classList.remove('cursor-left');
    cursorTarget.classList.remove('cursor-right');
    cursorTarget.style.cursor = 'auto';
  }
}

function updateMediaPosition() {
  if (!galleryItems.length) return;

  if (galleryItems.length > 1) {
    if (window.matchMedia('(min-width: 749px)').matches) {
      galleryItems.forEach(function (item, index) {
        item.style.transform = '';
        item.style.transition = '';
        if (index === currentIndex) {
          item.classList.add('active');
          item.classList.add('fade-in');
          setTimeout(function () {
            item.classList.remove('fade-in');
          }, 300);
          var media = getSlideMedia(item);
          if (media && media.tagName === 'VIDEO') {
            media.play();
          }
        } else {
          item.classList.remove('active');
          var media = getSlideMedia(item);
          if (media && media.tagName === 'VIDEO') {
            media.pause();
            media.currentTime = 0;
          }
        }
      });
    } else {
      const step = getSlideStep();
      const offset = currentIndex * -step;
      galleryItems.forEach(function (item, index) {
        item.style.transform = 'translateX(' + offset + 'px)';
        item.style.transition = isTouchDevice() ? 'transform 0.25s ease' : 'none';
        if (index === currentIndex) {
          item.classList.add('active');
        } else {
          item.classList.remove('active');
        }
      });
      if (!isTouchDevice() && galleryItems[currentIndex]) {
        galleryItems[currentIndex].classList.add('fade-in');
        setTimeout(function () {
          galleryItems[currentIndex].classList.remove('fade-in');
        }, 300);
      }
    }
    updateDots();
    } else {
    const item = galleryItems[0];
    if (!item) return;

    item.style.transform = '';
    item.style.transition = '';
    item.classList.add('active');

    if (!singleImageAnimated) {
      item.classList.add('fade-in');
      setTimeout(function () {
        item.classList.remove('fade-in');
      }, 300);
      singleImageAnimated = true;
    }

    var singleMedia = getSlideMedia(item);
    if (singleMedia && singleMedia.tagName === 'VIDEO') {
      singleMedia.play();
    }
  }

}

function handleArrowClick(direction) {
  if (!galleryItems.length || galleryItems.length <= 1) return;

  if (infiniteCarouselEnabled) {
    // Infinite carousel: just move to adjacent index (clones handle wrap)
    if (direction === 'left') {
      currentIndex = currentIndex - 1;
    } else if (direction === 'right') {
      currentIndex = currentIndex + 1;
    }
    updateMediaPosition();
    updateMobileGalleryHeight();
    // Schedule jump from clone to real after transition
    setTimeout(jumpFromCloneToReal, 350);
  } else {
    // Normal mode: wrap around
    if (direction === 'left') {
      currentIndex = (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    } else if (direction === 'right') {
      currentIndex = (currentIndex + 1) % galleryItems.length;
    }
    updateMediaPosition();
    updateMobileGalleryHeight();
  }
}

function handleSwipe(direction) {
  if (!galleryItems.length || galleryItems.length <= 1) return;
  // handleSwipe is only used on desktop (non-touch), so no infinite carousel
  if (direction === 'left') {
    currentIndex = (currentIndex + 1) % galleryItems.length;
  } else if (direction === 'right') {
    currentIndex = (currentIndex - 1 + galleryItems.length) % galleryItems.length;
  }
  updateMediaPosition();
  updateMobileGalleryHeight();
}


function scrollToVariantMedia(selectedVariant) {
  if (!selectedVariant || !galleryItems.length) return;
  const variantImage = selectedVariant.featured_image;
  if (!variantImage) return;

  const srcPart = variantImage.src;
  if (!srcPart) return;

  let foundIndex = -1;
  let foundItem = null;
  galleryItems.forEach(function (item, index) {
    // Skip clones when searching for variant image
    if (item.hasAttribute('data-clone')) return;
    var media = getSlideMedia(item);
    if (media && media.tagName === 'IMG' && media.src.indexOf(srcPart) !== -1) {
      foundIndex = index;
      foundItem = item;
    }
  });

  if (foundIndex !== -1) {
    // In vertical-scroll mode on desktop, smooth scroll to the image
    if (galleryMode === 'vertical-scroll' && foundItem && window.matchMedia('(min-width: 750px)').matches) {
      foundItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // In arrows mode (or mobile), switch to the image
    if (foundIndex !== currentIndex) {
      currentIndex = foundIndex;
      updateMediaPosition();
      updateMobileGalleryHeight();
    }
  }
}

// Vertical scroll mode - free scroll + arrow key navigation
let verticalScrollEnabled = false;
let currentImageIndex = 0;
let wheelHandler = null;
let galleryWheelHandler = null;
let imageGridMode = false;

function scrollToImage(index) {
  if (!galleryElement || !galleryItems[index]) return;

  // Native scroll mode - use scrollIntoView on the image
  if (typeof nativeScrollMode !== 'undefined' && nativeScrollMode) {
    galleryItems[index].scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    return;
  }

  // Hijacked scroll mode - scroll gallery container
  const containerHeight = galleryElement.clientHeight;
  const targetTop = index * containerHeight;

  galleryElement.scrollTo({
    top: targetTop,
    behavior: 'smooth'
  });
}

// Keyboard handler for arrow keys - snap to images
let keyboardHandler = null;

function handleKeyboardNav(e) {
  if (!galleryElement || !galleryItems.length) return;

  // Only handle arrow up/down
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

  // In grid mode, skip snap-to-image (let native scroll work)
  if (imageGridMode) return;

  const totalImages = galleryItems.length;

  // Calculate current image index based on scroll mode
  if (typeof nativeScrollMode !== 'undefined' && nativeScrollMode) {
    // Native scroll - find which image is most visible in viewport
    const viewportTop = window.scrollY;
    const viewportCenter = viewportTop + window.innerHeight / 3;

    for (let i = 0; i < galleryItems.length; i++) {
      const rect = galleryItems[i].getBoundingClientRect();
      const imgTop = rect.top + window.scrollY;
      const imgBottom = imgTop + rect.height;

      if (viewportCenter >= imgTop && viewportCenter < imgBottom) {
        currentImageIndex = i;
        break;
      }
    }
  } else {
    // Hijacked scroll - use gallery scroll position
    const containerHeight = galleryElement.clientHeight;
    const scrollTop = galleryElement.scrollTop;
    currentImageIndex = Math.round(scrollTop / containerHeight);
  }

  currentImageIndex = Math.max(0, Math.min(currentImageIndex, totalImages - 1));

  let nextIndex = currentImageIndex;

  if (e.key === 'ArrowDown' && currentImageIndex < totalImages - 1) {
    nextIndex = currentImageIndex + 1;
    e.preventDefault();
  } else if (e.key === 'ArrowUp' && currentImageIndex > 0) {
    nextIndex = currentImageIndex - 1;
    e.preventDefault();
  }

  if (nextIndex !== currentImageIndex) {
    currentImageIndex = nextIndex;
    scrollToImage(nextIndex);
  }
}



// Cleanup function for mobile gallery touch handlers
function cleanupMobileGallery() {
  // Cleanup infinite carousel first
  cleanupInfiniteCarousel();

  // Reset transform styles on gallery items (important for vertical-scroll mode)
  if (galleryItems && galleryItems.length) {
    galleryItems.forEach(function(item) {
      item.style.transform = '';
      item.style.transition = '';
    });
  }

  // Reset gallery height (dynamic height cleanup)
  if (galleryElement) {
    galleryElement.style.height = '';
    galleryElement.style.transition = '';
  }

  if (!mobileGalleryInitialized) return;

  if (galleryElement) {
    if (mobileTouchStartHandler) {
      galleryElement.removeEventListener('touchstart', mobileTouchStartHandler);
    }
    if (mobileTouchMoveHandler) {
      galleryElement.removeEventListener('touchmove', mobileTouchMoveHandler);
    }
    if (mobileTouchEndHandler) {
      galleryElement.removeEventListener('touchend', mobileTouchEndHandler);
    }
  }

  mobileTouchStartHandler = null;
  mobileTouchMoveHandler = null;
  mobileTouchEndHandler = null;
  mobileGalleryInitialized = false;
}

// Cleanup function for gallery click/arrow handlers
function cleanupGalleryHandlers() {
  if (prevArrow && prevArrowClickHandler) {
    prevArrow.removeEventListener('click', prevArrowClickHandler);
  }
  if (nextArrow && nextArrowClickHandler) {
    nextArrow.removeEventListener('click', nextArrowClickHandler);
  }
  if (galleryElement && galleryClickHandler) {
    galleryElement.removeEventListener('click', galleryClickHandler);
  }
  if (galleryElement && galleryMouseMoveHandler) {
    galleryElement.removeEventListener('mousemove', galleryMouseMoveHandler);
  }
  prevArrowClickHandler = null;
  nextArrowClickHandler = null;
  galleryClickHandler = null;
  galleryMouseMoveHandler = null;
}

// Setup infinite carousel (mobile only) - adds clones of first/last images
function setupInfiniteCarousel() {
  if (!galleryElement || !galleryItems || galleryItems.length <= 1) return;
  if (infiniteCarouselEnabled) return; // Already set up

  // Reset to first image before setup
  currentIndex = 0;

  realImageCount = galleryItems.length;

  // Clone first and last images
  var firstItem = galleryItems[0];
  var lastItem = galleryItems[realImageCount - 1];

  cloneFirst = firstItem.cloneNode(true);
  cloneLast = lastItem.cloneNode(true);

  // Mark clones for identification
  cloneFirst.setAttribute('data-clone', 'first');
  cloneLast.setAttribute('data-clone', 'last');

  // Insert clones: [lastClone, ...originals..., firstClone]
  galleryElement.insertBefore(cloneLast, firstItem);
  galleryElement.appendChild(cloneFirst);

  // Fix cloned images: if clone's img not loaded, add handler
  [cloneFirst, cloneLast].forEach(function(clone) {
    var img = clone.querySelector('.product__item-img');
    if (img && !img.complete) {
      img.onload = function() { clone.classList.add('loaded'); };
    } else if (img) {
      clone.classList.add('loaded');
    }
  });

  // Update galleryItems to include clones
  galleryItems = Array.from(galleryElement.querySelectorAll('.gallery-slide'));

  // Shift currentIndex by 1 to account for prepended clone
  currentIndex = currentIndex + 1;

  // Position gallery to show the real first image (not the clone)
  var initStep = getSlideStep();
  galleryItems.forEach(function(item, index) {
    item.style.transition = 'none';
    item.style.transform = 'translateX(' + (currentIndex * -initStep) + 'px)';
    // Set active class on current item
    if (index === currentIndex) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });

  infiniteCarouselEnabled = true;

  // Update dots to reflect current position
  updateDots();
}

// Cleanup infinite carousel - removes clones
function cleanupInfiniteCarousel() {
  if (!infiniteCarouselEnabled) return;

  // Remove clones from DOM
  if (cloneFirst && cloneFirst.parentNode) {
    cloneFirst.parentNode.removeChild(cloneFirst);
  }
  if (cloneLast && cloneLast.parentNode) {
    cloneLast.parentNode.removeChild(cloneLast);
  }

  cloneFirst = null;
  cloneLast = null;

  // Restore currentIndex to real position
  if (currentIndex > 0) {
    currentIndex = currentIndex - 1;
  }

  // Re-query gallery items (without clones)
  if (galleryElement) {
    galleryItems = Array.from(galleryElement.querySelectorAll('.gallery-slide:not([data-clone])'));
  }

  realImageCount = 0;
  infiniteCarouselEnabled = false;
}

// Get real index (for dots display) from current carousel index
function getRealIndex() {
  if (!infiniteCarouselEnabled) return currentIndex;
  // currentIndex 0 = clone of last (real index = realImageCount - 1)
  // currentIndex 1 = real first (real index = 0)
  // currentIndex realImageCount = real last (real index = realImageCount - 1)
  // currentIndex realImageCount + 1 = clone of first (real index = 0)
  if (currentIndex === 0) return realImageCount - 1;
  if (currentIndex === realImageCount + 1) return 0;
  return currentIndex - 1;
}

// Jump to real image after landing on a clone (called after transition)
function jumpFromCloneToReal() {
  if (!infiniteCarouselEnabled) return;

  var needsJump = false;
  var targetIndex = currentIndex;

  // If on clone of first (at the end), jump to real first
  if (currentIndex === realImageCount + 1) {
    targetIndex = 1; // Real first image
    needsJump = true;
  }
  // If on clone of last (at the beginning), jump to real last
  else if (currentIndex === 0) {
    targetIndex = realImageCount; // Real last image
    needsJump = true;
  }

  if (needsJump) {
    currentIndex = targetIndex;
    // Instant jump (no transition)
    var jumpStep = getSlideStep();
    galleryItems.forEach(function(item) {
      item.style.transition = 'none';
      item.style.transform = 'translateX(' + (currentIndex * -jumpStep) + 'px)';
    });
  }
}

// Cleanup function for vertical scroll mode
function cleanupVerticalScrollMode() {
  if (!verticalScrollEnabled) return;

  // Remove keyboard handler
  if (keyboardHandler) {
    document.removeEventListener('keydown', keyboardHandler);
    keyboardHandler = null;
  }

  // Remove wheel handler
  if (wheelHandler) {
    document.removeEventListener('wheel', wheelHandler);
    wheelHandler = null;
  }

  // Remove gallery wheel handler
  if (galleryWheelHandler && galleryElement) {
    galleryElement.removeEventListener('wheel', galleryWheelHandler);
    galleryWheelHandler = null;
  }

  // Reset state
  verticalScrollEnabled = false;
  currentImageIndex = 0;
  imageGridMode = false;
  if (typeof nativeScrollMode !== 'undefined') {
    nativeScrollMode = false;
  }

  // Reset gallery references (they may point to old DOM elements)
  galleryElement = null;
  galleryItems = [];
  currentIndex = 0;

  // Reset inertia absorption state
  atEdge = false;
  edgeDirection = 0;
  lastDeltaY = 0;
  if (edgeTimeout) {
    clearTimeout(edgeTimeout);
    edgeTimeout = null;
  }
}

function isPageAtTop() {
  return window.scrollY <= 5;
}

// Inertia absorption state
let atEdge = false;
let edgeDirection = 0; // 1 = bottom, -1 = top
let lastDeltaY = 0;
let edgeTimeout = null;
const INERTIA_THRESHOLD = 10; // Allow page scroll when deltaY drops below this

function resetEdgeState() {
  atEdge = false;
  edgeDirection = 0;
  lastDeltaY = 0;
  if (edgeTimeout) {
    clearTimeout(edgeTimeout);
    edgeTimeout = null;
  }
}

// Handle wheel on gallery - allow page scroll when at edges (with inertia absorption)
function handleGalleryWheel(e) {
  if (!verticalScrollEnabled || !galleryElement) return;

  const scrollTop = galleryElement.scrollTop;
  const maxScroll = galleryElement.scrollHeight - galleryElement.clientHeight;
  const atTop = scrollTop <= 1;
  const atBottom = scrollTop >= maxScroll - 1;
  const deltaY = Math.abs(e.deltaY);

  // If we're absorbing inertia at an edge
  if (atEdge) {
    // Direction changed - reset and allow normal scrolling
    if ((edgeDirection === 1 && e.deltaY < 0) || (edgeDirection === -1 && e.deltaY > 0)) {
      resetEdgeState();
      // Continue to normal handling below
    } else {
      // Still same direction - check if inertia has subsided
      if (deltaY < INERTIA_THRESHOLD) {
        // Inertia subsided - allow page scroll
        resetEdgeState();
        return;
      }
      // Still high inertia - absorb the event
      e.preventDefault();
      // Reset timeout
      if (edgeTimeout) clearTimeout(edgeTimeout);
      edgeTimeout = setTimeout(resetEdgeState, 150);
      return;
    }
  }

  // At bottom scrolling down - start absorbing inertia
  if (e.deltaY > 0 && atBottom) {
    // If deltaY is already small, allow page scroll immediately
    if (deltaY < INERTIA_THRESHOLD) {
      return;
    }
    // Start absorbing inertia
    atEdge = true;
    edgeDirection = 1;
    lastDeltaY = deltaY;
    e.preventDefault();
    edgeTimeout = setTimeout(resetEdgeState, 150);
    return;
  }

  // At top scrolling up - start absorbing inertia (if page is scrolled)
  if (e.deltaY < 0 && atTop && window.scrollY > 0) {
    if (deltaY < INERTIA_THRESHOLD) {
      return;
    }
    atEdge = true;
    edgeDirection = -1;
    lastDeltaY = deltaY;
    e.preventDefault();
    edgeTimeout = setTimeout(resetEdgeState, 150);
    return;
  }

  // Otherwise, scroll gallery and prevent page scroll
  e.preventDefault();
  galleryElement.scrollTop += e.deltaY;
}

// Handle wheel outside gallery - redirect to gallery when page at top
function handleWheelRedirect(e) {
  if (!verticalScrollEnabled || !galleryElement) return;

  // Only redirect when page is at top
  if (!isPageAtTop()) return;

  // If on gallery, handleGalleryWheel takes care of it
  if (e.target === galleryElement || galleryElement.contains(e.target)) return;

  // Check gallery bounds - if at edges, apply inertia absorption
  const scrollTop = galleryElement.scrollTop;
  const maxScroll = galleryElement.scrollHeight - galleryElement.clientHeight;
  const atTop = scrollTop <= 1;
  const atBottom = scrollTop >= maxScroll - 1;
  const deltaY = Math.abs(e.deltaY);

  // If absorbing inertia at edge
  if (atEdge) {
    if ((edgeDirection === 1 && e.deltaY < 0) || (edgeDirection === -1 && e.deltaY > 0)) {
      resetEdgeState();
    } else {
      if (deltaY < INERTIA_THRESHOLD) {
        resetEdgeState();
        return;
      }
      e.preventDefault();
      if (edgeTimeout) clearTimeout(edgeTimeout);
      edgeTimeout = setTimeout(resetEdgeState, 150);
      return;
    }
  }

  // Scrolling down at bottom - absorb inertia
  if (e.deltaY > 0 && atBottom) {
    if (deltaY < INERTIA_THRESHOLD) return;
    atEdge = true;
    edgeDirection = 1;
    e.preventDefault();
    edgeTimeout = setTimeout(resetEdgeState, 150);
    return;
  }

  // Scrolling up at top - absorb inertia
  if (e.deltaY < 0 && atTop) {
    if (deltaY < INERTIA_THRESHOLD) return;
    atEdge = true;
    edgeDirection = -1;
    e.preventDefault();
    edgeTimeout = setTimeout(resetEdgeState, 150);
    return;
  }

  // Redirect scroll to gallery
  e.preventDefault();
  galleryElement.scrollTop += e.deltaY;
}

// Native page scroll mode (two-columns + vertical-scroll)
let nativeScrollMode = false;

function initVerticalScrollMode() {
  // Cleanup any existing listeners first (important for theme editor)
  cleanupVerticalScrollMode();

  if (!galleryElement || !galleryItems.length || galleryItems.length <= 1) return;

  verticalScrollEnabled = true;
  currentImageIndex = 0;

  // Detect modes from data attributes
  const productInfo = queryProductElement('.product-info');
  imageGridMode = productInfo && productInfo.getAttribute('data-image-grid') === 'true';

  // Detect layout mode - two-columns uses native page scroll
  const productLayout = getComputedStyle(document.documentElement).getPropertyValue('--product-layout').trim();
  // Check if gallery has overflow visible (native scroll) or scroll (hijacked scroll)
  const galleryOverflow = getComputedStyle(galleryElement).overflowY;
  nativeScrollMode = galleryOverflow === 'visible';

  // Add keyboard navigation for arrow keys
  keyboardHandler = handleKeyboardNav;
  document.addEventListener('keydown', keyboardHandler);

  // Only add wheel handlers if NOT in native scroll mode
  if (!nativeScrollMode) {
    // Reset gallery scroll position to top
    galleryElement.scrollTop = 0;

    // Redirect wheel events to gallery when page is at top
    wheelHandler = handleWheelRedirect;
    document.addEventListener('wheel', wheelHandler, { passive: false });

    // Handle wheel on gallery - allow page scroll when at edges
    galleryWheelHandler = handleGalleryWheel;
    galleryElement.addEventListener('wheel', galleryWheelHandler, { passive: false });
  }
}

function initProductGallery(scope) {
  const container = scope || document;
  galleryElement = container.querySelector('#gallery');
  if (!galleryElement) return;

  // Detect gallery mode from data attribute
  const productInfoForGallery = queryProductElement('.product-info', container);
  if (productInfoForGallery) {
    galleryMode = productInfoForGallery.getAttribute('data-gallery-mode') || 'arrows';
  }

  // Cleanup any previous clones before re-init
  cleanupInfiniteCarousel();

  galleryItems = Array.from(galleryElement.querySelectorAll('.gallery-slide:not([data-clone])'));
  if (!galleryItems.length) return;

  // In vertical-scroll mode on desktop, setup scroll hijacking
  // On mobile, keep the regular swipe behavior
  if (galleryMode === 'vertical-scroll' && window.matchMedia('(min-width: 750px)').matches) {
    galleryItems.forEach(function (item) {
      item.classList.add('active');
      item.style.opacity = '1';
    });

    // Setup page scroll hijacking for vertical-scroll mode
    initVerticalScrollMode();
    return;
  }

  prevArrow = document.getElementById('prev-arrow');
  nextArrow = document.getElementById('next-arrow');
  navDotsContainer = document.getElementById('nav-dots');

  if (navDotsContainer) {
    navDotsContainer.innerHTML = '';
    // Store original count for dots (before infinite carousel adds clones)
    var dotsCount = galleryItems.length;
    for (var i = 0; i < dotsCount; i++) {
      (function(realIndex) {
        const dot = document.createElement('div');
        dot.classList.add('nav-dot');
        if (realIndex === currentIndex) {
          dot.classList.add('active');
        }
        dot.addEventListener('click', function () {
          // Convert real index to carousel index if infinite is enabled
          if (infiniteCarouselEnabled) {
            currentIndex = realIndex + 1; // +1 for prepended clone
          } else {
            currentIndex = realIndex;
          }
          updateMediaPosition();
          updateMobileGalleryHeight();
        });
        navDotsContainer.appendChild(dot);
      })(i);
    }
  }

  // Cleanup old handlers before adding new ones
  cleanupGalleryHandlers();

  if (prevArrow) {
    prevArrowClickHandler = function () {
      handleArrowClick('left');
    };
    prevArrow.addEventListener('click', prevArrowClickHandler);
  }

  if (nextArrow) {
    nextArrowClickHandler = function () {
      handleArrowClick('right');
    };
    nextArrow.addEventListener('click', nextArrowClickHandler);
  }

  // Add mousemove to gallery element for cursor changes on the whole block
  if (galleryElement && window.matchMedia('(min-width: 750px)').matches) {
    galleryMouseMoveHandler = function (event) {
      updateCursor(galleryElement, event);
    };
    galleryElement.addEventListener('mousemove', galleryMouseMoveHandler);
  }

  if (galleryElement && window.matchMedia('(min-width: 750px)').matches) {
    galleryClickHandler = function (event) {
      const rect = galleryElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const totalWidth = rect.width;
      const direction = x < totalWidth / 2 ? 'right' : 'left';
      handleSwipe(direction);
    };
    galleryElement.addEventListener('click', galleryClickHandler);
  }

    if (galleryElement && isTouchDevice() && !window.matchMedia('(min-width: 750px)').matches) {
    if (galleryItems.length <= 1) {
      return;
    }

    // Cleanup any existing mobile handlers first
    cleanupMobileGallery();

    // Setup infinite carousel for mobile
    setupInfiniteCarousel();

    let touchStartX = 0;
    let touchStartY = 0;
    let lastTouchX = 0;
    let isDragging = false;
    let dragInitialized = false;

    // Set initial height on mobile
    setTimeout(function() {
      updateMobileGalleryHeight();
    }, 100);

    // Recalculate height when images finish loading
    galleryItems.forEach(function(item) {
      var img = getSlideMedia(item);
      if (img && img.tagName === 'IMG' && !img.complete) {
        img.addEventListener('load', function() {
          updateMobileGalleryHeight();
        }, { once: true });
      }
    });

    // Store handlers for cleanup
    mobileTouchStartHandler = function (evt) {
      const touch = evt.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      lastTouchX = touch.clientX;
      isDragging = false;
      dragInitialized = false;
    };

    mobileTouchMoveHandler = function (evt) {
      const touch = evt.touches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      lastTouchX = touch.clientX;

      if (!dragInitialized) {
        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
          dragInitialized = true;
          isDragging = true;
        } else {
          return;
        }
      }

      if (!isDragging) return;

      evt.preventDefault();

      const slideStep = getSlideStep();
      const baseOffset = currentIndex * -slideStep;
      const offset = baseOffset + deltaX;

      galleryItems.forEach(function (item) {
        item.style.transition = 'none';
        item.style.transform = 'translateX(' + offset + 'px)';
      });

      // Dynamic height during swipe
      var progress = Math.min(Math.abs(deltaX) / slideStep, 1);
      // In infinite mode, just use adjacent indices (clones are there)
      var targetIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;
      // Clamp to valid range
      if (targetIndex < 0) targetIndex = 0;
      if (targetIndex >= galleryItems.length) targetIndex = galleryItems.length - 1;
      var currentHeight = getImageHeight(currentIndex);
      var targetHeight = getImageHeight(targetIndex);
      var interpolatedHeight = currentHeight + (targetHeight - currentHeight) * progress;
      setGalleryHeight(interpolatedHeight, false);
    };

    mobileTouchEndHandler = function () {
      if (!dragInitialized || !isDragging) {
        updateMediaPosition();
        updateMobileGalleryHeight();
        return;
      }

      const deltaX = lastTouchX - touchStartX;

      if (Math.abs(deltaX) > 50) {
        // Infinite carousel: just increment/decrement (clones handle wrap)
        if (deltaX < 0) {
          currentIndex = currentIndex + 1;
        } else {
          currentIndex = currentIndex - 1;
        }
      }

      galleryItems.forEach(function (item) {
        item.style.transition = '';
      });

      updateMediaPosition();
      updateMobileGalleryHeight();

      // Schedule jump from clone to real after transition completes
      setTimeout(jumpFromCloneToReal, 350);

      isDragging = false;
      dragInitialized = false;
    };

    galleryElement.addEventListener('touchstart', mobileTouchStartHandler, { passive: true });
    galleryElement.addEventListener('touchmove', mobileTouchMoveHandler, { passive: false });
    galleryElement.addEventListener('touchend', mobileTouchEndHandler, { passive: true });

    mobileGalleryInitialized = true;
  }

  const hasVariantButtons = queryProductElement('.option-value');
  if (!hasVariantButtons) {
    updateMediaPosition();
  }
}

/// Product tables ///
function initProductTables(scope) {
  const container = scope || document;
  container.querySelectorAll('table').forEach(function (table) {
    if (table.closest('.product-table')) return;
    const wrapper = document.createElement('div');
    wrapper.classList.add('product-table');
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}

/// Product heights ///
function setProductInfoHeight() {
  if (window.matchMedia('(min-width: 749px)').matches) {
    var headerSection = document.querySelector('.header-section');
    var headerSpacer = document.getElementById('headerSpacer');
    var productInfo = queryProductElement('.product-info');
    var productImages = queryProductElement('.figureImg');
    var productButtons = queryProductElement('.product-info__main');

    if (productInfo && productImages && productButtons) {
      // Get total header-group height (includes all sections like announcement bar + header)
      // Priority: CSS variable (set by main.js) > headerSpacer > header-group calculation > header-section
      var headerHeight = 0;

      // Try to get from CSS variable first (most accurate, set by main.js)
      var headerGroupHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--header-group-height');
      if (headerGroupHeightVar && headerGroupHeightVar.trim() !== '0px' && headerGroupHeightVar.trim() !== '') {
        headerHeight = parseInt(headerGroupHeightVar, 10) || 0;
      }

      // Fallback to headerSpacer if CSS variable not set
      if (headerHeight === 0 && headerSpacer && headerSpacer.offsetHeight > 0) {
        headerHeight = headerSpacer.offsetHeight;
      }

      // Fallback to calculating header-group height manually
      if (headerHeight === 0) {
        var headerGroup = document.querySelector('.shopify-section-group-header-group')
          || document.querySelector('#shopify-section-group-header-group')
          || document.querySelector('[id^="shopify-section-group-header"]');
        if (headerGroup) {
          var groupSections = headerGroup.querySelectorAll('.shopify-section');
          groupSections.forEach(function(section) {
            if (section.offsetHeight > 0) {
              headerHeight += section.offsetHeight;
            }
          });
        } else if (headerSection) {
          // Final fallback: just header-section
          headerHeight = headerSection.offsetHeight;
        }
      }

      // Note: --header-group-height is set by main.js calculateHeaderHeights()
      // We only read it here, don't overwrite

      // Check if native scroll mode (vertical-scroll + two-columns)
      var galleryOverflow = getComputedStyle(productImages).overflowY;
      var isNativeScrollMode = galleryOverflow === 'visible';

      if (isNativeScrollMode) {
        // Native scroll mode - don't set fixed heights, let CSS handle it
        productImages.style.height = '';
        productInfo.style.minHeight = '';
        productButtons.style.height = '';
      } else {
        // Standard mode - set fixed heights
        productImages.style.height = 'calc(95vh - ' + headerHeight + 'px)';
        productInfo.style.minHeight = 'calc(95vh - ' + headerHeight + 'px)';
        productButtons.style.height = 'calc(95vh - ' + headerHeight + 'px)';
      }
    }
  } else {
    var productInfoReset = queryProductElement('.product-info');
    var productImagesReset = queryProductElement('.figureImg');
    var productButtonsReset = queryProductElement('.product-info__main');
    if (productInfoReset) productInfoReset.style.minHeight = '';
    if (productButtonsReset) productButtonsReset.style.height = '';
    // Set dynamic height based on current image (mobile)
    updateMobileGalleryHeight();
  }

  if (galleryItems && galleryItems.length && selectedOptionsInitialized) {
    updateMediaPosition();
  }
}


function initProductHeights() {
  setProductInfoHeight();
  // Apply safe center after heights are calculated
  setTimeout(applySafeCenter, 50);
  if (!productHeightsInitialized) {
    productHeightsInitialized = true;
    window.addEventListener('resize', function() {
      setProductInfoHeight();
      applySafeCenter();
    });
  }
}

// Expose globally so header JS can trigger recalculation
window.setProductInfoHeight = setProductInfoHeight;

// Expose cart helpers so main.js can refresh after add-to-cart
window.fetchCartItems = fetchCartItems;
window.getCartQtyForVariant = getCartQtyForVariant;

// Listen for header height ready event
window.addEventListener('headerHeightReady', function() {
  setProductInfoHeight();
  setTimeout(applySafeCenter, 50);
});

/// Product accordions ///
function initAccordions(scope) {
  const container = scope || document;

  ['acc-pc', 'acc-mobile'].forEach(function (groupClass) {
    const accordions = queryProductElements('.' + groupClass, container);
    if (!accordions.length) return;

    if (accordions.length === 1) {
      accordions.forEach(function (acc) {
        acc.classList.remove('first-accordion', 'last-accordion', 'only-accordion');
      });
      accordions[0].classList.add('only-accordion');
    } else {
      accordions.forEach(function (acc) {
        acc.classList.remove('first-accordion', 'last-accordion', 'only-accordion');
      });
      accordions[0].classList.add('first-accordion');
      accordions[accordions.length - 1].classList.add('last-accordion');
    }
  });

  queryProductElements('.accordion-container', container).forEach(function (accordionContainer) {
    const toggleButton = accordionContainer.querySelector('.accordion-toggle');
    const content = accordionContainer.querySelector('.accordion-content');
    if (!toggleButton || !content) return;

    if (toggleButton.dataset.accordionBound === 'true') return;
    toggleButton.dataset.accordionBound = 'true';

    const isPc = accordionContainer.classList.contains('acc-pc');
    const groupSelector = isPc ? '.acc-pc' : '.acc-mobile';

    toggleButton.addEventListener('click', function () {
      const isOpen = toggleButton.getAttribute('aria-expanded') === 'true';

      queryProductElements(groupSelector).forEach(function (containerEl) {
        const otherToggle = containerEl.querySelector('.accordion-toggle');
        const otherContent = containerEl.querySelector('.accordion-content');
        if (!otherToggle || !otherContent) return;

        otherToggle.setAttribute('aria-expanded', 'false');
        otherContent.style.maxHeight = null;
      });

      if (!isOpen) {
        toggleButton.setAttribute('aria-expanded', 'true');
        content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        toggleButton.setAttribute('aria-expanded', 'false');
        content.style.maxHeight = null;
      }
    });
  });
}

/// Price ///
/// Price ///
function updatePriceDisplay(selectedVariant) {
  const variants = getProductVariants();
  if (!variants.length) return;

  const variantForPrice = selectedVariant || variants[0];

  const priceCurrentEls = document.querySelectorAll('[data-product-price-current]');
  if (!priceCurrentEls.length) return;

  priceCurrentEls.forEach(function (priceCurrentEl) {
    priceCurrentEl.textContent = formatPrice(variantForPrice.price);
    priceCurrentEl.classList.remove('product-price__current--sale');

    const container = priceCurrentEl.closest('.product-price-block');
    const priceCompareEl = container
      ? container.querySelector('[data-product-price-compare]')
      : null;

    if (!priceCompareEl) return;

    const hasSale =
      variantForPrice.compare_at_price &&
      variantForPrice.compare_at_price > variantForPrice.price;

    if (hasSale) {
      priceCompareEl.textContent = formatPrice(variantForPrice.compare_at_price);
      priceCompareEl.classList.remove('product-price__compare--hidden');
      priceCurrentEl.classList.add('product-price__current--sale');
    } else {
      priceCompareEl.textContent = '';
      priceCompareEl.classList.add('product-price__compare--hidden');
    }
  });
}



/// Product variants ///
function formatPrice(priceInCents) {
  const symbol = getCurrencySymbol();
  return symbol + (priceInCents / 100).toFixed(2);
}

function findMatchingVariant() {
  const variants = getProductVariants();
  if (!variants.length) return null;

  return (
    variants.find(function (variant) {
      return (
        Object.keys(selectedOptions).every(function (optionKey) {
          return variant[optionKey] === selectedOptions[optionKey];
        }) && variant.available
      );
    }) || null
  );
}

function updateActiveStates() {
  queryProductElements('.option-value').forEach(function (button) {
    const optionIndex = button.getAttribute('data-option');
    const key = 'option' + optionIndex;
    if (selectedOptions[key] === button.getAttribute('data-value')) {
      button.classList.add('active');
    } else {
      button.classList.remove('active');
    }
  });
}

function updateOptionsAvailability() {
  const variants = getProductVariants();
  if (!variants.length) return;

  const strings = getProductStrings();
  const addToCartButton = queryProductElement('.product-info__cart-btn');
  const allVariantsSoldOut = !variants.some(function (variant) {
    return variant.available;
  });

  if (addToCartButton && allVariantsSoldOut) {
    addToCartButton.textContent = strings.soldOut;
    addToCartButton.disabled = true;
    queryProductElements('.option-value').forEach(function (button) {
      button.classList.add('disabled');
    });
    return;
  }

  queryProductElements('.option-value').forEach(function (button) {
    const optionIndex = button.getAttribute('data-option');
    const value = button.getAttribute('data-value');
    const key = 'option' + optionIndex;

    const isAvailable = variants.some(function (variant) {
      let matchesOtherSelectedOptions = true;
      Object.keys(selectedOptions).forEach(function (selectedOptionKey) {
        if (selectedOptionKey !== key) {
          if (variant[selectedOptionKey] !== selectedOptions[selectedOptionKey]) {
            matchesOtherSelectedOptions = false;
          }
        }
      });
      return matchesOtherSelectedOptions && variant.available && variant[key] === value;
    });

    if (!isAvailable) {
      button.classList.add('disabled');
    } else {
      button.classList.remove('disabled');
    }
  });
}

function updateVariantAndPrice() {
  const strings = getProductStrings();
  const variants = getProductVariants();
  if (!variants.length) return;

  const matchingVariant = findMatchingVariant();
  const cartButton = queryProductElement('.product-info__cart-btn');
  if (!cartButton) return;

  if (matchingVariant) {
    currentVariant = matchingVariant;

    const form = document.getElementById('AddToCartForm');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = matchingVariant.id;
      }

      // Update quantity wrapper data attrs for inventory limits
      var qtyWrapper = form.querySelector('.quantity-wrapper-product');
      if (qtyWrapper) {
        qtyWrapper.dataset.variantId = matchingVariant.id;
        qtyWrapper.dataset.inventoryQuantity = matchingVariant.inventory_quantity || 0;
        qtyWrapper.dataset.inventoryManagement = matchingVariant.inventory_management || '';
        qtyWrapper.dataset.inventoryPolicy = matchingVariant.inventory_policy || 'deny';
        // Reset qty to 1 on variant change
        var qtyInput = qtyWrapper.querySelector('.qty-value');
        if (qtyInput) qtyInput.value = 1;
      }
    }

    cartButton.disabled = false;
    if (cartButton.dataset.showPrice === 'true') {
      const formattedPrice = formatPrice(matchingVariant.price);
      cartButton.innerHTML = strings.addToCart + ' — ' + formattedPrice;
    } else {
      cartButton.innerHTML = strings.addToCart;
    }

    scrollToVariantMedia(matchingVariant);

    ensureSomeImageVisible();
  } else {
    cartButton.disabled = true;
    cartButton.textContent = strings.soldOut;

    ensureSomeImageVisible();
  }

  // Update sticky bar button state
  var stickyBtn = document.getElementById('StickyATCButton');
  if (stickyBtn) {
    if (matchingVariant) {
      stickyBtn.disabled = false;
      stickyBtn.textContent = strings.addToCart;
    } else {
      stickyBtn.disabled = true;
      stickyBtn.textContent = strings.soldOut;
    }
  }

  updatePriceDisplay(matchingVariant);
  if (window.updateAllQuantityLimits) window.updateAllQuantityLimits();
}
function ensureSomeImageVisible() {
  if (galleryItems && galleryItems.length) {
    const hasActive = galleryItems.some(item => item.classList.contains('active'));
    if (!hasActive) {
      currentIndex = 0;
      updateMediaPosition();
    }
  }
}




function initializeSelections() {
  const variants = getProductVariants();
  if (!variants.length) return;

  const firstAvailableVariant = variants.find(function (variant) {
    return variant.available;
  });

  const baseVariant = firstAvailableVariant || variants[0];

  selectedOptions = {};
  baseVariant.options.forEach(function (optionValue, index) {
    const optionIndex = index + 1;
    const optionButtons = queryProductElements(
      '.option-value[data-option="' + optionIndex + '"]'
    );

    optionButtons.forEach(function (button) {
      if (button.getAttribute('data-value') === optionValue) {
        button.classList.add('active');
      }
    });

    selectedOptions['option' + optionIndex] = optionValue;
  });

  selectedOptionsInitialized = true;
  updateOptionsAvailability();
  updateVariantAndPrice();
}


function attachVariantHandlers(scope) {
  const container = scope || document;
  const variantButtons = queryProductElements('.option-value', container);
  if (!variantButtons.length) return;

  variantButtons.forEach(function (button) {
    if (button.dataset.variantBound === 'true') return;
    button.dataset.variantBound = 'true';

    button.addEventListener('click', function () {
      const optionIndex = this.getAttribute('data-option');
      const value = this.getAttribute('data-value');
      const key = 'option' + optionIndex;

      if (this.classList.contains('disabled')) {
        return;
      }

      selectedOptions[key] = value;
      updateActiveStates();
      updateOptionsAvailability();
      updateVariantAndPrice();
    });
  });
}

function initProductVariants(scope) {
  const variants = getProductVariants();
  if (!variants.length) return;

  if (!selectedOptionsInitialized) {
    initializeSelections();
  } else {
    updateActiveStates();
    updateOptionsAvailability();
    updateVariantAndPrice();
  }

  attachVariantHandlers(scope);
}

/// Quantity limit (inventory-aware) ///
function fetchCartItems(callback) {
  fetch('/cart.js')
    .then(function(r) { return r.json(); })
    .then(function(cart) {
      cartItemsCache = cart.items || [];
      if (callback) callback();
    })
    .catch(function() {
      cartItemsCache = [];
      if (callback) callback();
    });
}

function getCartQtyForVariant(variantId) {
  if (!cartItemsCache || !variantId) return 0;
  var total = 0;
  cartItemsCache.forEach(function(item) {
    if (String(item.id) === String(variantId) || String(item.variant && item.variant.id) === String(variantId)) {
      total += item.quantity;
    }
  });
  return total;
}

/// Sticky Add to Cart ///
var stickyPanelOpen = false;

function updateStickyPanel() {
  var panel = document.getElementById('StickyATCPanel');
  if (!panel) return;

  var panelImg = document.getElementById('StickyATCPanelImage');
  if (panelImg && currentVariant) {
    var imgSrc = (currentVariant.featured_image && currentVariant.featured_image.src) || panelImg.src;
    panelImg.src = imgSrc;
  }

  // Update option labels
  Object.keys(selectedOptions).forEach(function(key) {
    var pos = key.replace('option', '');
    var label = panel.querySelector('[data-sticky-option="' + pos + '"]');
    if (label) label.textContent = selectedOptions[key];
  });

  // Update active/disabled states in dropdowns
  var variants = getProductVariants();
  panel.querySelectorAll('.sticky-atc-panel__dropdown-item').forEach(function(item) {
    var optPos = item.getAttribute('data-option');
    var val = item.getAttribute('data-value');
    var key = 'option' + optPos;

    // Active state
    if (selectedOptions[key] === val) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }

    // Availability
    var isAvailable = variants.some(function(variant) {
      var matchesOthers = true;
      Object.keys(selectedOptions).forEach(function(selKey) {
        if (selKey !== key && variant[selKey] !== selectedOptions[selKey]) {
          matchesOthers = false;
        }
      });
      return matchesOthers && variant.available && variant[key] === val;
    });

    if (isAvailable) {
      item.classList.remove('disabled');
    } else {
      item.classList.add('disabled');
    }
  });
}

function openStickyPanel() {
  var panel = document.getElementById('StickyATCPanel');
  var stickyBtn = document.getElementById('StickyATCButton');
  if (!panel || !stickyBtn) return;

  updateStickyPanel();
  panel.classList.add('visible');
  stickyPanelOpen = true;

  var strings = getProductStrings();
  stickyBtn.textContent = strings.confirm;
}

function closeStickyPanel() {
  var panel = document.getElementById('StickyATCPanel');
  var stickyBtn = document.getElementById('StickyATCButton');
  if (!panel) return;

  panel.classList.remove('visible');
  stickyPanelOpen = false;

  if (stickyBtn) {
    var strings = getProductStrings();
    if (currentVariant) {
      stickyBtn.textContent = strings.addToCart;
    } else {
      stickyBtn.textContent = strings.soldOut;
    }
  }
}

function stickyAddToCart() {
  var stickyBtn = document.getElementById('StickyATCButton');
  var mainForm = document.getElementById('AddToCartForm');
  var idInput = mainForm ? mainForm.querySelector('input[name="id"]') : null;
  if (!stickyBtn || !idInput) return;

  var variantId = idInput.value;
  var originalText = stickyBtn.innerHTML;

  stickyBtn.disabled = true;
  stickyBtn.innerHTML = '<span class="btn-loading-dots"><span></span><span></span><span></span></span>';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(function(r) { if (!r.ok) throw new Error('Error'); return r.json(); })
  .then(function() { return fetch('/cart.js'); })
  .then(function(r) { return r.json(); })
  .then(function(cart) {
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { count: cart.item_count } }));
    stickyBtn.disabled = false;
    closeStickyPanel();

    if (window.cartType === 'drawer') {
      document.dispatchEvent(new CustomEvent('cart:open-drawer'));
    } else {
      window.location.href = '/cart';
    }
  })
  .catch(function() {
    stickyBtn.innerHTML = originalText;
    stickyBtn.disabled = false;
  });
}

function initStickyATC() {
  var stickyBar = document.getElementById('StickyATC');
  if (!stickyBar) return;

  // Don't show sticky bar if all variants are sold out
  var variants = getProductVariants();
  if (variants.length && !variants.some(function(v) { return v.available; })) return;

  stickyBar.style.display = '';

  // IntersectionObserver on buy buttons
  var buyButtons = document.querySelector('.buy-buttons-wrapper');
  if (buyButtons) {
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('visible');
          if (stickyPanelOpen) closeStickyPanel();
        } else if (entry.boundingClientRect.bottom < 0) {
          // Only show when scrolled PAST the buy buttons (they're above viewport)
          stickyBar.classList.add('visible');
        } else {
          stickyBar.classList.remove('visible');
        }
      });
    }, { threshold: 0 });
    observer.observe(buyButtons);
  }

  // Button: first click opens panel, second click (Confirm) adds to cart
  var stickyBtn = document.getElementById('StickyATCButton');
  if (stickyBtn) {
    stickyBtn.addEventListener('click', function() {
      if (stickyPanelOpen) {
        stickyAddToCart();
      } else {
        openStickyPanel();
      }
    });
  }

  // Panel close button
  var panelClose = document.getElementById('StickyATCPanelClose');
  if (panelClose) {
    panelClose.addEventListener('click', closeStickyPanel);
  }

  // Dropdown toggles
  var panel = document.getElementById('StickyATCPanel');
  if (panel) {
    panel.querySelectorAll('.sticky-atc-panel__option-toggle').forEach(function(toggle) {
      toggle.addEventListener('click', function(e) {
        e.stopPropagation();
        var optionGroup = this.closest('.sticky-atc-panel__option');
        var wasOpen = optionGroup.classList.contains('open');
        // Close all dropdowns
        panel.querySelectorAll('.sticky-atc-panel__option').forEach(function(g) {
          g.classList.remove('open');
        });
        if (!wasOpen) {
          optionGroup.classList.add('open');
        }
      });
    });

    // Dropdown item click — select variant
    panel.querySelectorAll('.sticky-atc-panel__dropdown-item').forEach(function(item) {
      item.addEventListener('click', function() {
        if (this.classList.contains('disabled')) return;
        var optPos = this.getAttribute('data-option');
        var val = this.getAttribute('data-value');
        selectedOptions['option' + optPos] = val;

        // Close dropdown
        this.closest('.sticky-atc-panel__option').classList.remove('open');

        // Sync everything
        updateActiveStates();
        updateOptionsAvailability();
        updateVariantAndPrice();
        updateStickyPanel();
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
      if (!panel.contains(e.target)) {
        panel.querySelectorAll('.sticky-atc-panel__option').forEach(function(g) {
          g.classList.remove('open');
        });
      }
    });
  }
}

/// Init hooks ///
function initProductMain(scope) {
  initProductGallery(scope);
  initProductTables(scope);
  initAccordions(scope);
  initProductHeights();
  initProductVariants(scope);
  if (!verticalScrollEnabled) {
    updateMediaPosition();
  }
}

document.addEventListener('DOMContentLoaded', function () {
  // Init gallery and variants immediately (don't wait for cart fetch)
  initProductMain(document);
  // Fetch cart data in background for quantity limits and sticky ATC
  fetchCartItems(function() {
    if (window.updateAllQuantityLimits) window.updateAllQuantityLimits();
    initStickyATC();
  });
});

document.addEventListener('shopify:section:load', function (event) {
  // Cleanup before reinitializing
  cleanupMobileGallery();
  cleanupVerticalScrollMode();
  cleanupGalleryHandlers();
  initProductMain(event.target);
});

document.addEventListener('shopify:section:unload', function (event) {
  // Cleanup when section is unloaded (important for theme editor)
  cleanupMobileGallery();
  cleanupVerticalScrollMode();
  cleanupGalleryHandlers();
});

// Listen for viewport changes (desktop <-> mobile) to reinitialize gallery
// This is important for vertical-scroll mode which behaves differently on mobile
let lastWasDesktop = window.matchMedia('(min-width: 750px)').matches;
const mediaQueryList = window.matchMedia('(min-width: 750px)');

function handleViewportChange(e) {
  const isDesktop = e.matches;

  // Only reinitialize if we crossed the breakpoint
  if (isDesktop !== lastWasDesktop) {
    lastWasDesktop = isDesktop;

    // Cleanup all handlers before reinitializing
    cleanupMobileGallery();
    cleanupVerticalScrollMode();
    cleanupGalleryHandlers();
    initProductGallery(document);
    setProductInfoHeight();
    setTimeout(applySafeCenter, 50);
  }
}

// Use addEventListener for modern browsers, addListener for older ones
if (mediaQueryList.addEventListener) {
  mediaQueryList.addEventListener('change', handleViewportChange);
} else if (mediaQueryList.addListener) {
  mediaQueryList.addListener(handleViewportChange);
}

// Fallback: debounced resize handler for customizer iframes where media query might not fire
let resizeTimeout = null;
window.addEventListener('resize', function() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(function() {
    const isDesktop = window.innerWidth >= 750;
    if (isDesktop !== lastWasDesktop) {
      lastWasDesktop = isDesktop;
      cleanupMobileGallery();
      cleanupVerticalScrollMode();
      cleanupGalleryHandlers();
      initProductGallery(document);
      setProductInfoHeight();
    }
  }, 150);
});

})(); // End IIFE
